/**
 * Der zentrale Eingangskorb — `INBOX-001`, nach `07` §7.4.
 *
 * Er ist eine **Arbeitsliste**, kein Postfach. Der Unterschied steht im
 * Dokument: „Er ersetzt nicht die Quellsysteme, sondern zeigt je Vorgang
 * Herkunft und Eingangszeit, sichere Vorschau und Dokumenttyp, vorgeschlagenen
 * Kunden und Auftrag mit Begründung, Validierungs- oder Verarbeitungsfehler,
 * Bearbeiter und aktuellen Prüfstatus, Aktionen zum Zuordnen, Zurückstellen,
 * Ablehnen oder Erstellen eines neuen Auftrags."
 *
 * Drei Dinge, die hier bewusst so und nicht anders gebaut sind:
 *
 * **Ein Vorschlag bleibt ein Vorschlag.** Er steht in einem eigenen Kasten mit
 * Begründung und einem Wort statt einer Prozentzahl — nie als Prozentzahl, die
 * sich wie eine Messung liest und zum Durchwinken einlädt. Er belegt die
 * Zuordnungsauswahl vor, aber die Bestätigung bleibt ein eigener Griff: `07`
 * §7.4 verlangt, dass er „bis zur Bestätigung sichtbar als Vorschlag" bleibt,
 * nicht dass er unsichtbar bleibt.
 *
 * (Hier stand vorher das Gegenteil — „nicht in die Zuordnungsfelder
 * eingetragen" —, während der Code zwei Absätze weiter unten genau das tut und
 * es auch begründet. Der Code hatte recht.)
 *
 * **Und er wird gerechnet, nicht abgelesen.** `vorschlagFuer` ruft die
 * Heuristik aus `packages/demo-data`; Regeln, keine KI, weil ADR-017 sie hinter
 * dem Piloten hält und ein Modell hier keine bessere Begründung hätte, sondern
 * eine unprüfbare.
 *
 * **Quarantäne zeigt nichts.** ADR-023 lässt den Signaturscanner beanstanden,
 * und eine hilfsbereite Vorschau wäre genau hier der Fehler. Der Vorgang
 * erscheint mit Absender und Betreff — mehr nicht, und der Anhang bleibt
 * unerreichbar.
 *
 * **Sammelbearbeitung ist eng gefasst.** `07` §7.4: „nur für gleichartige,
 * bereits geprüfte Vorgänge". Deshalb greift die Mehrfachauswahl hier nur bei
 * Vorgängen im Zustand `in_pruefung` und nur innerhalb einer Eingangsart.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router";

import type { Eingangsart, Eingangsstatus, Eingangsvorgang } from "@hwc/demo-data";
import { berechneVorschlag, findeAuftrag, findeKunde, findePerson } from "@hwc/demo-data";
import { formatResourceRef } from "@hwc/contracts";
import type { ResourceRef } from "@hwc/contracts";

import { darfEingangBearbeiten, darfQuarantaeneFreigeben } from "@hwc/auth";
import { Dialog, Knopf, Zustand } from "@hwc/ui";

import { useSitzung } from "../../src/daten/Sitzung.js";
import { legeAuftragAn, ordneZu } from "../../src/daten/vorgaenge.js";
import {
  Eingangsabzeichen,
  Leer,
  Liste,
  Zeile,
  datum,
  euro,
  mehrzahl,
  sicherheitWort,
  uhrzeit,
  zumAuftrag,
} from "../../src/bereiche/bausteine.js";

const ARTTEXT: Record<Eingangsart, string> = {
  email: "E-Mail",
  dokument: "Dokument",
  erechnung: "E-Rechnung",
  upload: "Upload",
};

type Sicht = "offen" | "erledigt" | "alle";

/**
 * Der Vorschlag zu einem Vorgang — gerechnet, nicht abgelesen.
 *
 * **Eine Stelle, nicht drei.** Der Vorschlag wird an drei Orten gebraucht: im
 * Kasten mit der Begründung, in der Vorbelegung der Auftragsauswahl und in der
 * des Kundenfeldes beim neuen Auftrag. Rechnete jeder für sich, könnten Kasten
 * und Feld verschiedene Aufträge nennen — und die Bürokraft bestätigte etwas
 * anderes, als sie gelesen hat.
 *
 * `berechneVorschlag` liefert oft **nichts**, und das ist richtig: Ein
 * Vorschlag, der bei jedem Vorgang irgendetwas nennt, macht in zwei Wochen den
 * guten unsichtbar.
 */
function vorschlagFuer(
  bestand: Parameters<typeof berechneVorschlag>[0],
  vorgang: Eingangsvorgang,
) {
  return berechneVorschlag(bestand, {
    betreff: vorgang.betreff,
    absender: vorgang.absender,
    ...(vorgang.vorschau ? { vorschau: vorgang.vorschau } : {}),
  });
}

export function Eingang() {
  const { bestand } = useSitzung();
  const [sicht, setzeSicht] = useState<Sicht>("offen");
  const [gewaehlt, setzeGewaehlt] = useState<string | undefined>(undefined);

  const vorgaenge = useMemo(() => {
    const offen: readonly Eingangsstatus[] = ["neu", "in_pruefung", "quarantaene"];
    return bestand.eingang
      .filter((v) => {
        if (sicht === "alle") return true;
        const istOffen = offen.includes(v.status);
        return sicht === "offen" ? istOffen : !istOffen;
      })
      .slice()
      .sort((x, y) => y.eingegangen.localeCompare(x.eingegangen));
  }, [bestand, sicht]);

  const detail = gewaehlt
    ? bestand.eingang.find((v) => v.ref.opaqueId === gewaehlt)
    : undefined;

  return (
    <>
      <div className="bereichskopf">
        <h1>Eingang</h1>
        <span className="bereichskopf__zusatz">
          {mehrzahl(vorgaenge.length, "Vorgang", "Vorgänge")}
        </span>
      </div>

      <div className="filter">
        {(
          [
            { wert: "offen", text: "Zu bearbeiten" },
            { wert: "erledigt", text: "Erledigt" },
            { wert: "alle", text: "Alle" },
          ] as const
        ).map((f) => (
          <button
            key={f.wert}
            type="button"
            className={`filter__knopf${sicht === f.wert ? " filter__knopf--aktiv" : ""}`}
            onClick={() => setzeSicht(f.wert)}
            aria-pressed={sicht === f.wert}
          >
            {f.text}
          </button>
        ))}
      </div>

      <p className="notiz">
        Die Rechnungen selbst — Steller, Betrag, Profil, Befund und Hash — stehen
        gesammelt unter <Link to="/erechnungen">E-Rechnungen</Link>. Hier steht der
        Vorgang, dort die Rechnung: zwei Fragen, und die zweite stellt man, wenn der
        Korb längst leer ist.
      </p>

      {vorgaenge.length === 0 ? (
        <Leer text="Nichts zu tun. Der Eingangskorb ist in dieser Sicht leer." />
      ) : (
        <Liste>
          {vorgaenge.map((v) => (
            <Zeile
              key={v.ref.opaqueId}
              titel={
                <button
                  type="button"
                  className="zeile__oeffnen"
                  onClick={() =>
                    setzeGewaehlt(gewaehlt === v.ref.opaqueId ? undefined : v.ref.opaqueId)
                  }
                  aria-expanded={gewaehlt === v.ref.opaqueId}
                >
                  {v.betreff}
                </button>
              }
              unterzeile={
                <>
                  {ARTTEXT[v.art]} · {v.absender} · {datum(v.eingegangen)}{" "}
                  {uhrzeit(v.eingegangen)}
                  {v.bearbeiter
                    && ` · ${findePerson(bestand, v.bearbeiter)?.name ?? ""}`}
                </>
              }
              abzeichen={<Eingangsabzeichen status={v.status} />}
            />
          ))}
        </Liste>
      )}

      {/*
        **Der `key` ist kein Aufräumen, er behebt einen Fehlgriff.**

        Gemessen am 05.09.2026 im Browser: Wer die Lieferantenrechnung öffnete
        (Vorschlag: Auftrag 2490, die Auswahl damit vorbelegt) und dann zur
        nächsten Rechnung wechselte, sah deren Daten — aber in der
        Auftragsauswahl stand weiter 2490. Ein Griff auf „Zuordnen" hätte die
        Kabelwerk-Rechnung an den Wallbox-Auftrag gehängt, ohne dass irgendwo
        etwas rot geworden wäre.

        Grund: React montiert eine Komponente an derselben Stelle im Baum nicht
        neu, also überlebt jeder `useState`-Anfangswert den Vorgangswechsel.
        Der `key` sagt: Ein anderer Vorgang ist eine andere Sache. Damit gehen
        auch offene Dialoge und stehengebliebene Absagen zurück auf Anfang.

        Die Auftragsakte hat dasselbe Muster und ist trotzdem heil — dort
        setzt der Reiterwechsel die Kinder ohnehin zurück. Verlassen sollte man
        sich darauf nicht: Das ist eine Nebenwirkung, keine Zusage.
      */}
      {detail && <Vorgangsdetail key={detail.ref.opaqueId} vorgang={detail} />}
    </>
  );
}

function Vorgangsdetail({ vorgang }: { vorgang: Eingangsvorgang }) {
  const { bestand } = useSitzung();

  const rechnung = bestand.rechnungen.find(
    (r) => formatResourceRef(r.eingangsvorgang) === formatResourceRef(vorgang.ref),
  );

  const vorschlag = vorschlagFuer(bestand, vorgang);
  const zugeordnet = findeAuftrag(bestand, vorgang.zugeordneterAuftrag);
  const inQuarantaene = vorgang.status === "quarantaene";

  return (
    <section className="karte">
      <h2 style={{ marginTop: 0 }}>{vorgang.betreff}</h2>

      {/*
        Der Befund steht **vor** allem anderen. Bei einer Rechnung, deren XML
        und PDF auseinandergehen, ist die Reihenfolge auf dem Bildschirm die
        Reihenfolge, in der jemand liest — und wer erst den Betrag sieht und
        dann den Befund, hat den Betrag schon geglaubt.
      */}
      {vorgang.fehler && (
        <Zustand art={inQuarantaene ? "fehler" : "warnung"} text={vorgang.fehler} />
      )}

      {inQuarantaene ? (
        <p className="notiz notiz--fehler">
          <strong>Keine Vorschau.</strong> Der Anhang wurde bei der Eingangsprüfung
          beanstandet und ist gesperrt (ADR-023). Weder Inhalt noch Download stehen zur
          Verfügung; eine Freigabe kann nur der Administrator veranlassen.
        </p>
      ) : (
        vorgang.vorschau && <p>{vorgang.vorschau}</p>
      )}

      {rechnung && !inQuarantaene && (
        <>
          <h3>Rechnungsdaten</h3>
          {/*
            Die Felder, die `07` §7.16 der Bürokraft zusagt: Rechnungssteller,
            Rechnungsnummer, Datum, Betrag, unterstütztes Profil und
            Validierungsstatus.
          */}
          <dl className="felder">
            <div className="feld">
              <dt className="feld__name">Rechnungssteller</dt>
              <dd className="feld__wert">{rechnung.steller}</dd>
            </div>
            <div className="feld">
              <dt className="feld__name">Rechnungsnummer</dt>
              <dd className="feld__wert">{rechnung.rechnungsnummer}</dd>
            </div>
            <div className="feld">
              <dt className="feld__name">Datum</dt>
              <dd className="feld__wert">{datum(rechnung.datum)}</dd>
            </div>
            <div className="feld">
              <dt className="feld__name">Betrag</dt>
              <dd className="feld__wert">{euro(rechnung.betragCent)}</dd>
            </div>
            <div className="feld">
              <dt className="feld__name">Profil</dt>
              <dd className="feld__wert">{rechnung.profil}</dd>
            </div>
            <div className="feld">
              <dt className="feld__name">Validierung</dt>
              <dd className="feld__wert">
                {rechnung.validierung === "gueltig"
                  ? "gültig"
                  : rechnung.validierung === "mit-anmerkungen"
                    ? "gültig, mit Anmerkungen"
                    : "ungültig"}
              </dd>
            </div>
          </dl>

          {rechnung.befunde.length > 0 && (
            <>
              <h3>Befunde</h3>
              <ul>
                {rechnung.befunde.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </>
          )}

          {/*
            `07` §7.16: „Das Original, sein Hash und das Validierungsprotokoll
            bleiben zusammen auffindbar." Der Hash steht deshalb sichtbar und
            auswählbar — wer die Unverändertheit belegen muss, liest ihn vor
            oder kopiert ihn.
          */}
          <p className="notiz">
            <strong>Original unverändert in Paperless.</strong>{" "}
            <code style={{ userSelect: "all", wordBreak: "break-all" }}>
              {rechnung.originalHash}
            </code>
          </p>
        </>
      )}

      {vorschlag && !vorgang.zugeordneterAuftrag && (
        <>
          <h3>Vorschlag</h3>
          {/*
            `07` §7.4: „Automatische Vorschläge bleiben bis zur Bestätigung
            sichtbar als Vorschlag." Deshalb ein eigener Kasten, eine Begründung
            und ein Wort statt einer Prozentzahl — „86 %" liest sich wie eine
            Messung und lädt zum Durchwinken ein.
          */}
          <div className="notiz notiz--warnung">
            <p style={{ margin: 0 }}>
              <strong>Noch nicht zugeordnet.</strong> Die Erkennung schlägt vor:{" "}
              {vorschlag.kunde && (
                <>{findeKunde(bestand, vorschlag.kunde)?.name}</>
              )}
              {vorschlag.kunde && vorschlag.auftrag && ", "}
              {vorschlag.auftrag && (
                <>Auftrag {findeAuftrag(bestand, vorschlag.auftrag)?.nummer}</>
              )}{" "}
              — {sicherheitWort(vorschlag.sicherheit)}.
            </p>
            <p style={{ marginBottom: 0 }}>Begründung: {vorschlag.begruendung}</p>
          </div>
        </>
      )}

      {zugeordnet && (
        <p className="notiz">
          <strong>Zugeordnet</strong> zu{" "}
          <Link to={zumAuftrag(zugeordnet.nummer)}>
            Auftrag {zugeordnet.nummer} — {zugeordnet.titel}
          </Link>
          .
        </p>
      )}

      <Aktionen vorgang={vorgang} />
    </section>
  );
}

/**
 * Die vier Aktionen aus `07` §7.4 — zuordnen, zurückstellen, ablehnen, neuen
 * Auftrag erstellen.
 *
 * **Sie tun jetzt etwas.** Bis zum 05.09.2026 standen sie hier abgeschaltet,
 * mit einem Satz darunter, dass der schreibende Pfad fehlt. Das war ehrlich
 * und hat den Korb zur Lesehilfe gemacht: Ob dieser Ablauf im Büroalltag
 * trägt, lässt sich an einer Liste nicht beurteilen, die man nicht abarbeiten
 * kann.
 *
 * **Der Vorschlag belegt nur vor.** `07` §7.4: „Automatische Vorschläge
 * bleiben bis zur Bestätigung sichtbar als Vorschlag." Er füllt deshalb die
 * Auswahl, und die Bestätigung bleibt ein eigener Griff. Ein Vorschlag, der
 * sich selbst bestätigt, ist eine Automatik mit Fußnote.
 *
 * **Ablehnen fragt nach, die anderen drei nicht.** `05` §5.12 verlangt für
 * auslösende Aktionen eine sichtbare Bestätigung. Zuordnen, Zurückstellen und
 * Aufnehmen sind mit einem Griff rückgängig zu machen; eine Abweisung trifft
 * als einzige eine Aussage über den Vorgang.
 */
function Aktionen({ vorgang }: { vorgang: Eingangsvorgang }) {
  const { bestand, person, rollen, fuehreAus } = useSitzung();
  const [absage, setzeAbsage] = useState<string | null>(null);
  const [ablehnenOffen, setzeAblehnen] = useState(false);
  const [neuOffen, setzeNeu] = useState(false);
  const [zielAuftrag, setzeZiel] = useState<string>(
    () => vorschlagFuer(bestand, vorgang)?.auftrag?.opaqueId ?? "",
  );

  const inQuarantaene = vorgang.status === "quarantaene";
  const erledigt = vorgang.status === "zugeordnet" || vorgang.status === "abgewiesen";

  if (!darfEingangBearbeiten(rollen)) {
    return (
      <p className="notiz">
        Ihre Rolle bearbeitet den Eingangskorb nicht. Nach <code>07</code> §7.2 liegt
        er bei Büro und Disposition.
      </p>
    );
  }

  if (inQuarantaene) {
    return (
      <>
        <h3>Aktionen</h3>
        <p className="notiz notiz--fehler">
          <strong>Gesperrt.</strong>{" "}
          {darfQuarantaeneFreigeben(rollen)
            ? "Als Administrator könnten Sie den Vorgang freigeben — der Weg dafür "
              + "gehört zur Schadsoftware-Prüfung aus ADR-023 und nicht in den "
              + "Eingangskorb."
            : "Eine Freigabe kann nur der Administrator veranlassen (ADR-023)."}
        </p>
      </>
    );
  }

  const ausfuehren = (schritt: Parameters<typeof fuehreAus>[0]) => {
    setzeAbsage(fuehreAus(schritt) ?? null);
  };

  return (
    <>
      <h3>Aktionen</h3>

      {absage && <Zustand art="warnung" text={absage} />}

      {erledigt ? (
        <>
          <p className="notiz">
            Dieser Vorgang ist{" "}
            {vorgang.status === "zugeordnet" ? "zugeordnet" : "abgewiesen"}. Er bleibt
            auffindbar, damit ihn niemand ein zweites Mal bearbeitet und sich wundert.
          </p>
          <div className="filter">
            <Knopf
              type="button"
              art="neben"
              onClick={() =>
                ausfuehren((v) =>
                  ordneZu({
                    vorgaenge: v,
                    vorgangRef: vorgang.ref,
                    status: "in_pruefung",
                    bearbeiter: person.ref,
                    bisher: vorgang.status,
                    rollen,
                  }),
                )
              }
            >
              Wieder aufnehmen
            </Knopf>
          </div>
        </>
      ) : (
        <>
          <label className="feld">
            <span className="feld__name">Auftrag</span>
            <select
              className="suchfeld"
              value={zielAuftrag}
              onChange={(e) => setzeZiel(e.target.value)}
            >
              <option value="">— bitte wählen —</option>
              {bestand.auftraege
                .slice()
                .sort((a, b) => b.nummer.localeCompare(a.nummer))
                .map((a) => (
                  <option key={a.ref.opaqueId} value={a.ref.opaqueId}>
                    {a.nummer} — {a.titel}
                  </option>
                ))}
            </select>
          </label>

          <div className="filter">
            <Knopf
              type="button"
              art="haupt"
              disabled={!zielAuftrag}
              onClick={() => {
                const auftrag = bestand.auftraege.find(
                  (a) => a.ref.opaqueId === zielAuftrag,
                );
                if (!auftrag) return;
                ausfuehren((v) =>
                  ordneZu({
                    vorgaenge: v,
                    vorgangRef: vorgang.ref,
                    status: "zugeordnet",
                    auftrag: auftrag.ref,
                    bearbeiter: person.ref,
                    bisher: vorgang.status,
                    rollen,
                  }),
                );
              }}
            >
              Zuordnen
            </Knopf>

            <Knopf type="button" art="neben" onClick={() => setzeNeu(!neuOffen)}>
              Neuen Auftrag anlegen
            </Knopf>

            <Knopf
              type="button"
              art="neben"
              onClick={() =>
                ausfuehren((v) =>
                  ordneZu({
                    vorgaenge: v,
                    vorgangRef: vorgang.ref,
                    status: "zurueckgestellt",
                    bearbeiter: person.ref,
                    bisher: vorgang.status,
                    rollen,
                  }),
                )
              }
            >
              Zurückstellen
            </Knopf>

            <Knopf type="button" art="still" onClick={() => setzeAblehnen(true)}>
              Ablehnen
            </Knopf>
          </div>

          {neuOffen && (
            <NeuerAuftrag
              vorgang={vorgang}
              beiAnlage={(titel, kunde, gewerk) => {
                let neuerRef: string | undefined;
                const grund = fuehreAus((v) => {
                  const ergebnis = legeAuftragAn({
                    vorgaenge: v,
                    bestand,
                    titel,
                    kunde,
                    verantwortlich: person.ref,
                    gewerk,
                    beschreibung:
                      "Entstanden aus dem Eingangsvorgang: " + vorgang.betreff,
                  });
                  if (ergebnis.ok) {
                    neuerRef = ergebnis.vorgaenge.neueAuftraege.at(-1)?.ref.opaqueId;
                  }
                  return ergebnis;
                });
                setzeAbsage(grund ?? null);
                // Gleich vorbelegen: Wer den Auftrag eben angelegt hat, will
                // den Vorgang genau dorthin zuordnen.
                if (neuerRef) setzeZiel(neuerRef);
                setzeNeu(false);
              }}
              beiAbbruch={() => setzeNeu(false)}
            />
          )}

          <Dialog
            offen={ablehnenOffen}
            beiSchliessen={() => setzeAblehnen(false)}
            titel="Vorgang ablehnen?"
          >
            {/*
              `05` §5.12: Was die Aktion tut, steht vor der Ausführung da —
              nicht nur im Knopfnamen.
            */}
            <p>
              Der Vorgang wird als abgewiesen markiert. Er bleibt auffindbar und die
              Quellressource bleibt unverändert im Quellsystem; er verschwindet nur
              aus der Arbeitsliste.
            </p>
            <div className="filter">
              <Knopf
                type="button"
                art="haupt"
                onClick={() => {
                  ausfuehren((v) =>
                    ordneZu({
                      vorgaenge: v,
                      vorgangRef: vorgang.ref,
                      status: "abgewiesen",
                      bearbeiter: person.ref,
                      bisher: vorgang.status,
                    rollen,
                    }),
                  );
                  setzeAblehnen(false);
                }}
              >
                Ja, ablehnen
              </Knopf>
              <Knopf type="button" art="neben" onClick={() => setzeAblehnen(false)}>
                Abbrechen
              </Knopf>
            </div>
          </Dialog>
        </>
      )}
    </>
  );
}

/**
 * Das Formular für einen Auftrag aus einem Eingangsvorgang heraus.
 *
 * Betreff und vorgeschlagener Kunde stehen vorbelegt darin. Beides ist schon
 * da, und Abtippen ist die häufigste Stelle, an der etwas verrutscht — eine
 * Auftragsnummer landet später in einer Mail an den Kunden.
 */
function NeuerAuftrag({
  vorgang,
  beiAnlage,
  beiAbbruch,
}: {
  vorgang: Eingangsvorgang;
  beiAnlage: (titel: string, kunde: ResourceRef, gewerk?: string) => void;
  beiAbbruch: () => void;
}) {
  const { bestand } = useSitzung();
  const [titel, setzeTitel] = useState(vorgang.betreff);
  const [kunde, setzeKunde] = useState(
    () => vorschlagFuer(bestand, vorgang)?.kunde?.opaqueId ?? "",
  );
  const [gewerk, setzeGewerk] = useState("");

  return (
    <section className="karte">
      <h3 style={{ marginTop: 0 }}>Neuer Auftrag</h3>
      <label className="feld">
        <span className="feld__name">Titel</span>
        <input
          className="suchfeld"
          type="text"
          value={titel}
          onChange={(e) => setzeTitel(e.target.value)}
        />
      </label>
      <label className="feld">
        <span className="feld__name">Kunde</span>
        <select
          className="suchfeld"
          value={kunde}
          onChange={(e) => setzeKunde(e.target.value)}
        >
          <option value="">— bitte wählen —</option>
          {bestand.kunden.map((k) => (
            <option key={k.ref.opaqueId} value={k.ref.opaqueId}>
              {k.nummer} — {k.name}
            </option>
          ))}
        </select>
      </label>
      <label className="feld">
        <span className="feld__name">Gewerk (freiwillig)</span>
        <input
          className="suchfeld"
          type="text"
          value={gewerk}
          onChange={(e) => setzeGewerk(e.target.value)}
          placeholder="z. B. Installation"
        />
      </label>
      <div className="filter">
        <Knopf
          type="button"
          art="haupt"
          disabled={!titel.trim() || !kunde}
          onClick={() => {
            const treffer = bestand.kunden.find((k) => k.ref.opaqueId === kunde);
            if (treffer) beiAnlage(titel, treffer.ref, gewerk.trim() || undefined);
          }}
        >
          Auftrag anlegen
        </Knopf>
        <Knopf type="button" art="neben" onClick={beiAbbruch}>
          Abbrechen
        </Knopf>
      </div>
    </section>
  );
}
